import classNames from 'classnames';
import React from 'react';

import type {
  ExtensionProps,
  GeneralContext,
  PluginProps,
  RicosExtension,
  RicosExtensionConfig,
} from './../ricos-types';
import { isNodeSelected } from './../_shared/is-node-selected';
import {
  getNodeWidth,
  getPluginContainerClassNames,
  getPluginFocusClassNames,
  getPluginPlacementClassNames,
} from './../_shared/node-styles';
import type { TiptapAdapterServices } from './../tiptap-editor/initializeTiptapAdapter';

import { createRicosFunctionalExtension } from './create-ricos-extension';

const getStylesHOC =
  (isTextWrap: boolean, context: Omit<GeneralContext, 'portal'>, nodesWithoutSelection: string[]) =>
  (Component: React.ComponentType<PluginProps>) => {
    const Styles = (props: PluginProps) => {
      const { isMobile } = context;
      const shouldShowSelection = !nodesWithoutSelection.includes(props.node.type.name);
      const isFocused =
        shouldShowSelection && isNodeSelected(props.editor.state, props.node.attrs.id);

      const className = classNames(
        getPluginPlacementClassNames(props.node, isTextWrap),
        getPluginContainerClassNames(isMobile),
        shouldShowSelection && getPluginFocusClassNames(isFocused)
      );

      const width = getNodeWidth(props.node, isMobile);

      const style: React.CSSProperties = {
        width: width ? `min(100%, ${width}px)` : undefined,
      };

      return (
        <div className={className} style={style}>
          <Component {...props} />
        </div>
      );
    };
    Styles.displayName = 'StylesHoc';

    return Styles;
  };

export const styles = createRicosFunctionalExtension({
  type: 'extension',
  groups: [],
  name: 'style',
  reconfigure: (
    config: RicosExtensionConfig,
    extensions: RicosExtension[],
    ricosProps: ExtensionProps,
    _settings: Record<string, unknown>,
    services: TiptapAdapterServices
  ) => ({
    ...config,
    addNodeHoc() {
      return {
        nodeTypes: extensions
          .filter(extension => !extension.groups.includes('custom-styles'))
          .map(({ name }) => name),
        priority: 100,
        nodeHoc: getStylesHOC(
          ricosProps.isTextWrap ?? true,
          services.context,
          extensions
            .filter(({ groups }) => groups.includes('hide-selection'))
            .map(({ name }) => name)
        ),
      };
    },
  }),
  createExtensionConfig() {
    return {
      name: this.name,
      priority: 30,
    };
  },
});
